import {
  WSButton,
  WSCard,
  WSDivider,
  WSElement,
  WSEmptyState,
  WSFlexBox,
  WSList,
  WSPillProps,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { WorkDefinition, WorkItem } from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React, { useMemo } from "react";
import { queriesWorkLogs } from "@query";
import { filterActiveWorkItems } from "../../utils/filterActiveWorkItems";
import { AcceptedValuesResources } from "../FormPartialAttributes/helpers";
import { Item } from "./Item";
import { useModalAddItem } from "./ModalAddItem";
import styles from "./index.module.scss";

type Props = {
  shimmer?: boolean;
  noEdit?: boolean;
  payerEdit?: boolean;

  workLogId: string;
  payerId: string;
  payerPayeeEngagementId: string;

  workItems: WorkItem[];
  workDefinitions: WorkDefinition[];
  resources: AcceptedValuesResources;

  contractorName?: string;
  currentUserRole?: "payer" | "payee";
  otherPartyName?: string;
  onViewHistory?: () => void;
  onEdit?: () => void;
  statusPill?: WSPillProps;
  overlay?: boolean;
};

export const CardWorkItems: React.FC<Props> = props => {
  const modalAddItem = useModalAddItem();
  const queryWorkLog = queriesWorkLogs.getWorkLog.useQuery({
    workLogId: props.workLogId
  });
  const activeWorkItems = useMemo(
    () => filterActiveWorkItems(props.workItems),
    [props.workItems]
  );

  return (
    <WSElement>
      <WSList gap="M">
        <WSElement>
          <WSSectionToolbar
            title="Work summary"
            pill={props.statusPill}
            buttons={[
              ...(props.onViewHistory
                ? [
                    {
                      kind: "Link" as const,
                      label: "View history",
                      onClick: props.onViewHistory
                    }
                  ]
                : []),
              ...(props.onEdit
                ? [
                    {
                      kind: "Link" as const,
                      label: "Edit",
                      onClick: props.onEdit
                    }
                  ]
                : [])
            ]}
          />
          <WSText.ParagraphSm color="gray500">
            Work logging for this assignment is only available in U.S. Dollars
            (USD).
          </WSText.ParagraphSm>
        </WSElement>

        {activeWorkItems.length === 0 && !props.shimmer ? (
          <WSEmptyState
            type="documents"
            orientation="horizontal"
            size="S"
            title="No work added yet"
            description="Add work items to your work log to get started"
            hasBorder
            bgColor="white"
            actions={
              !props.noEdit
                ? {
                    buttons: [
                      {
                        kind: "Secondary",
                        label: "Add work",
                        onClick: () => {
                          modalAddItem.open({
                            workLogId: props.workLogId,
                            payerId: props.payerId,
                            workDefinitions: props.workDefinitions,
                            engagementId: props.payerPayeeEngagementId,
                            resources: props.resources
                          });
                        }
                      }
                    ]
                  }
                : undefined
            }
          />
        ) : (
          <WSElement
            className={props.overlay ? styles.overlayContainer : undefined}
          >
            {props.overlay && <WSElement className={styles.overlay} />}
            <WSCard className={styles.itemsCard} shimmer={props.shimmer}>
              {activeWorkItems.map((workItem, index) => (
                <React.Fragment key={workItem.id}>
                  {index > 0 && <WSDivider />}
                  <WSElement px="XL">
                    <Item
                      noEdit={props.noEdit}
                      payerEdit={props.payerEdit}
                      showViewHistory={!!props.onViewHistory}
                      contractorName={props.contractorName}
                      workItem={workItem}
                      workDefinitions={props.workDefinitions}
                      resources={props.resources}
                      currentUserRole={props.currentUserRole}
                      otherPartyName={props.otherPartyName}
                      workLogStatus={queryWorkLog.data?.status}
                      lastSubmittedAt={queryWorkLog.data?.events?.inReviewAt}
                    />
                  </WSElement>
                </React.Fragment>
              ))}

              {!props.noEdit && (
                <WSElement px="XL">
                  <WSButton.Link
                    icon="plus-circle"
                    onClick={() => {
                      modalAddItem.open({
                        workLogId: props.workLogId,
                        payerId: props.payerId,
                        workDefinitions: props.workDefinitions,
                        engagementId: props.payerPayeeEngagementId,
                        resources: props.resources
                      });
                    }}
                  >
                    Add work
                  </WSButton.Link>
                </WSElement>
              )}
            </WSCard>
            <WSCard mt="M" shimmer={props.shimmer}>
              <WSFlexBox
                wrap="nowrap"
                justify="space-between"
                alignItems="center"
              >
                <WSText color="gray500" weight="medium">
                  Total amount (USD)
                </WSText>
                <WSText.Heading5>
                  {formatMoney(queryWorkLog.data?.totalAmount || 0, "USD")}
                </WSText.Heading5>
              </WSFlexBox>
            </WSCard>
          </WSElement>
        )}
      </WSList>
    </WSElement>
  );
};
